import { useCallback, useState } from "react";
import { countryCodeToCountryName } from "@/utils/countries.util";
import { getTotalLength } from "@/utils/helper.util";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SearchFilterItem } from "@/types/SearchFilterItem.type";
import { useLocationAutocomplete } from "@/hooks/api/search/useLocationAutocomplete";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Chip from "@/components/atoms/Chip";
import FilterSectionItem from "@/components/molecules/filter/FilterSectionItem";
import LocationSelectItem from "@/components/molecules/filter/LocationSelectItem";
import FormCheckbox from "@/components/molecules/form/FormCheckbox";
import FormComboBox from "@/components/molecules/form/FormComboBox";

const LocationFilter = ({ name }: SearchFilterItem) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const [locationIncludeQuery, setLocationIncludeQuery] = useState("");
  const [locationExcludeQuery, setLocationExcludeQuery] = useState("");

  const showTerminated: boolean = watch("showTerminated");
  const locationFilters = watch(["locationInclude", "locationExclude"]);

  const {
    query: locationInclude,
    setDebounceQuery: setLocationInclude,
    isTyping: locationIncludeTyping,
  } = useDebounceQuery();

  const {
    query: locationExclude,
    setDebounceQuery: setLocationExclude,
    isTyping: locationExcludeTyping,
  } = useDebounceQuery();

  const { data: includedLocationData, isLoading: isLoadingLocationInclude } = useLocationAutocomplete({
    query: locationInclude,
    showTerminated,
  });

  const { data: excludedLocationData, isLoading: isLoadingLocationExclude } = useLocationAutocomplete({
    query: locationExclude,
    showTerminated,
  });

  const country = useCallback((countryCode: string) => countryCodeToCountryName(countryCode), []);

  return (
    <FilterSectionItem
      id={name}
      title={t("search.filter.location.title")}
      filterCount={getTotalLength(locationFilters)}
      tooltip={t("search.filter.location.tooltip")}
    >
      <FormComboBox
        query={locationIncludeQuery}
        setQuery={(query) => {
          setLocationIncludeQuery(query);
          setLocationInclude(query);
        }}
        multiple
        optionsSearchable={false}
        showOptionsOnFocus
        name="locationInclude"
        placeholder={t("search.filter.location.placeholder")}
        options={includedLocationData || []}
        isLoading={isLoadingLocationInclude || locationIncludeTyping}
        renderOption={(option) => <LocationSelectItem location={option} />}
        renderChip={(option, removeOption) => (
          <Chip
            data-testid={`selected-option-${option.id}`}
            size={"xs"}
            color={"natural"}
            key={option.id}
            label={option.name === option.country_code ? country(option.name) : option.name}
            className={"max-w-28"}
            onRemove={() => removeOption(option)}
          />
        )}
      />
      <FormCheckbox name={"hq"} label={t("search.filter.location.hqOnly")} />

      <FormComboBox
        query={locationExcludeQuery}
        setQuery={(query) => {
          setLocationExcludeQuery(query);
          setLocationExclude(query);
        }}
        multiple
        optionsSearchable={false}
        showOptionsOnFocus
        name="locationExclude"
        label={t("search.filter.location.exclude")}
        placeholder={t("search.filter.location.placeholder")}
        options={excludedLocationData || []}
        isLoading={isLoadingLocationExclude || locationExcludeTyping}
        renderOption={(option) => <LocationSelectItem location={option} />}
        renderChip={(option, removeOption) => (
          <Chip
            data-testid={`selected-option-${option.id}`}
            size={"xs"}
            color={"natural"}
            key={option.id}
            label={option.name === option.country_code ? country(option.name) : option.name}
            className={"max-w-28"}
            onRemove={() => removeOption(option)}
          />
        )}
      />
    </FilterSectionItem>
  );
};

export default LocationFilter;
